import { AddTask } from "@mui/icons-material";
import { Box, Button, Card, CardHeader, Divider, Stack, Typography, useTheme } from "@mui/material";
import _ from "lodash";
import { DateTime } from "luxon";
import { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  AccordionSkeleton,
  AccordionTitle,
  CreateTaskItemModal,
  TaskItem,
  ViewList,
} from "../../../../components";
import { TASK_ITEM_STATUSES, TeamTaskItemsGroupByOption } from "../../../../constants";
import { useSession } from "../../../../hooks";
import { TaskItemDto, useGetTeam, useGetTeamTaskItemsTree } from "../../../../http";
import { useTeamImplementationSettings } from "../hooks";
import { TeamTaskItemsFilterButton } from "./TeamTaskItemsFilterButton";
import { TeamTaskItemsTable } from "./TeamTaskItemsTable";

interface IProps {
  teamSlug: string;
  isTableView: boolean;
}

const TeamTaskItemsList = (props: IProps) => {
  const { displayName: sessionUserDisplayName, userId: sessionUserGuid } = useSession();
  const { settings } = useTeamImplementationSettings(props.teamSlug);
  const { data: team, isLoading: isLoadingTeam } = useGetTeam(props.teamSlug, sessionUserGuid);
  const {
    data: tasks,
    isLoading: isLoadingTaskItems,
    fetchTaskItemSubtasks,
  } = useGetTeamTaskItemsTree(
    props.teamSlug,
    {
      filters: {
        teamSlug: [props.teamSlug],
        status: settings.taskItemsFilterByStatusOptions?.map((x) => x.toString()) ?? null,
      },
      sortProperty: settings.taskItemsSortByOption,
      sortDirection: settings.taskItemsSortByDirection,
    },
    {
      filters: {
        status: settings.taskItemsFilterByStatusOptions?.map((x) => x.toString()) ?? null,
      },
      sortProperty: settings.taskItemsSortByOption,
      sortDirection: settings.taskItemsSortByDirection,
    },
  );
  const [groupedTasks, setGroupedTasks] = useState<Map<string, TaskItemDto[]> | undefined>(undefined);
  const [isCreateTaskItemModalOpen, setIsCreateTaskItemModalOpen] = useState(false);
  const theme = useTheme();

  const groupByFunction = useCallback(
    (item: TaskItemDto) => {
      switch (settings.taskItemsGroupByOption) {
        case TeamTaskItemsGroupByOption.None:
          return "All Tasks";
        case TeamTaskItemsGroupByOption.DueDate: {
          if (item.dueDate == null) {
            return "No Due Date";
          }

          const dueDate = DateTime.fromISO(item.dueDate);

          if (dueDate < DateTime.now()) {
            return "Past Due";
          } else if (dueDate < DateTime.now().plus({ week: 1 })) {
            return "Due Soon";
          } else {
            return "Due Later";
          }
        }
        case TeamTaskItemsGroupByOption.InFocus:
          return item.owners.some((x) => x.isFocused) ? "Tasks in Focus" : "All Other Tasks";
        case TeamTaskItemsGroupByOption.Owner: {
          if (item.owners.length === 0) {
            return "No Owner";
          }

          return item.owners
            .map((x) => x.user.displayName)
            .sort((a, b) => a.localeCompare(b))
            .join(", ");
        }
        case TeamTaskItemsGroupByOption.PrimaryOwner: {
          if (item.primaryOwner == null) {
            return "No Primary Owner";
          }

          return item.primaryOwner.displayName;
        }
        case TeamTaskItemsGroupByOption.Status: {
          return TASK_ITEM_STATUSES.find((x) => x.id === item.currentStatus)?.name;
        }
        default:
          return;
      }
    },
    [settings.taskItemsGroupByOption],
  );

  const groupSortByFunction = useCallback(
    (groupName: string) => {
      switch (settings.taskItemsGroupByOption) {
        case TeamTaskItemsGroupByOption.DueDate:
          return ["Past Due", "Due Soon", "Due Later", "No Due Date"].indexOf(groupName);
        case TeamTaskItemsGroupByOption.InFocus:
          return [groupName !== "Tasks in Focus", groupName];
        case TeamTaskItemsGroupByOption.Owner:
          return [groupName !== sessionUserDisplayName, groupName];
        case TeamTaskItemsGroupByOption.PrimaryOwner:
          return [groupName !== sessionUserDisplayName, groupName];
        case TeamTaskItemsGroupByOption.Status:
          return _.sortBy(TASK_ITEM_STATUSES, (x) => x.sortOrder)
            .map((x) => x.name)
            .indexOf(groupName);
        default:
          return groupName;
      }
    },
    [sessionUserDisplayName, settings.taskItemsGroupByOption],
  );

  useEffect(() => {
    if (isLoadingTaskItems) {
      // Clear local state when loading new data
      setGroupedTasks(undefined);
    }
  }, [isLoadingTaskItems]);

  useEffect(() => {
    if (tasks !== undefined) {
      // Group and filter task items
      const taskItems = _.groupBy(tasks, groupByFunction);

      // Sort task item group names
      const sortedGroupNames = _.sortBy(_.keys(taskItems), groupSortByFunction);

      // Create a map of sorted groups
      const groupedTaskItems = sortedGroupNames.reduce((groupedTaskItems, groupName) => {
        groupedTaskItems.set(groupName, taskItems[groupName]);
        return groupedTaskItems;
      }, new Map());

      setGroupedTasks(groupedTaskItems);
    }
  }, [tasks, groupByFunction, groupSortByFunction]);

  const isLoading = isLoadingTaskItems || isLoadingTeam;

  return (
    <>
      <Card>
        <CardHeader
          title={<Typography variant="h6">Tasks</Typography>}
          action={
            <Stack direction="row" spacing={1} divider={<Divider orientation="vertical" flexItem />}>
              {(team?.isAdministrator || team?.isMember) && (
                <Button
                  variant="tertiary"
                  disabled={isLoading}
                  startIcon={<AddTask />}
                  onClick={() => setIsCreateTaskItemModalOpen(true)}
                >
                  Add New Task
                </Button>
              )}
              {team && <TeamTaskItemsFilterButton teamSlug={team.slug} disabled={isLoading} />}
            </Stack>
          }
          sx={{ pb: 0 }}
        />
        <Box sx={{ p: 0.5 }}>
          {isLoading || team === undefined || groupedTasks === undefined ? (
            <AccordionSkeleton isTableView={props.isTableView} />
          ) : (
            <Stack spacing={0.5}>
              {groupedTasks.size === 0 ? (
                <Typography variant="placeholder" sx={{ px: 0.5 }}>
                  Nothing here yet.
                </Typography>
              ) : (
                Array.from(groupedTasks.keys()).map((groupName, i) => (
                  <Accordion
                    key={i}
                    title={(expanded) => (
                      <AccordionTitle
                        title={groupName}
                        subtitle={expanded ? undefined : `(${groupedTasks.get(groupName)?.length})`}
                      />
                    )}
                    backgroundColor={theme.palette.grey[100]}
                    accordionDetailsSx={{ px: props.isTableView ? 0 : 0.5, pb: props.isTableView ? 0 : 0.5 }}
                    sx={{
                      mb: groupName === sessionUserDisplayName ? "20px !important" : undefined,
                    }}
                  >
                    {props.isTableView ? (
                      <TeamTaskItemsTable
                        tableId={`teamTaskItemsList-${team.slug}`}
                        taskItems={groupedTasks.get(groupName) ?? []}
                        fetchTaskItemSubtasks={fetchTaskItemSubtasks}
                        enableActions={team.isAdministrator || team.isMember}
                      />
                    ) : (
                      <ViewList
                        dataSource={groupedTasks.get(groupName)}
                        renderItem={(item) => (
                          <TaskItem key={item.guid} taskItem={item} openDrawerOnClick userCanEdit={item.userCanEdit} sx={{ px: 0.5 }} />
                        )}
                      />
                    )}
                  </Accordion>
                ))
              )}
            </Stack>
          )}
        </Box>
      </Card>

      <CreateTaskItemModal
        visible={isCreateTaskItemModalOpen}
        onCreated={() => setIsCreateTaskItemModalOpen(false)}
        onCancelled={() => setIsCreateTaskItemModalOpen(false)}
        defaultSelectedTeamSlug={props.teamSlug}
      />
    </>
  );
};

export { TeamTaskItemsList };
